import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Building2, LogIn, ReceiptText, Search } from "lucide-react";
import { toast } from "sonner";
import { api, shortDate } from "../lib/api";

export default function ClientAccountsView({ onOpenClient }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [openingId, setOpeningId] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    api
      .get("/master/clients")
      .then(({ data }) => setClients(data))
      .catch((error) => toast.error(error.response?.data?.detail || "Could not load client accounts"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return clients;
    const q = query.toLowerCase();
    return clients.filter((c) => [c.business_name, c.name, c.email].some((v) => `${v ?? ""}`.toLowerCase().includes(q)));
  }, [clients, query]);

  const open = async (client, options) => {
    setOpeningId(client.id);
    try {
      await api.post(`/master/impersonate/${client.id}`);
      toast.success(options?.createInvoice ? `Billing ${client.business_name || client.email}` : `Opening ${client.business_name || client.email}`);
      onOpenClient?.(options);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not open this account");
      setOpeningId(null);
    }
  };

  const factoryReset = async () => {
    if (confirmText !== "RESET") return;
    setResetting(true);
    try {
      const { data } = await api.post("/master/factory-reset", { confirm: "RESET" });
      toast.success(`Reset complete — removed ${data.removed_accounts} account(s). Reloading…`);
      setTimeout(() => window.location.reload(), 1200);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not reset the app");
      setResetting(false);
    }
  };

  return (
    <section className="panel module-panel" data-testid="client-accounts-view">
      <div className="danger-zone" data-testid="danger-zone">
        <div className="danger-zone-head">
          <span className="danger-zone-icon"><AlertTriangle size={18} /></span>
          <div>
            <h3>Start fresh — reset to a brand-new app</h3>
            <p>Permanently deletes <strong>all client accounts, team-created test entries and demo data</strong> (customers, suppliers, products, invoices, orders, expenses, payments, setup &amp; upgrade requests). Your master account stays. This cannot be undone.</p>
          </div>
        </div>
        <div className="danger-zone-action">
          <input
            data-testid="factory-reset-confirm-input"
            className="inline-search"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type RESET to confirm"
          />
          <button
            className="danger-button"
            data-testid="factory-reset-button"
            disabled={confirmText !== "RESET" || resetting}
            onClick={factoryReset}
          >
            {resetting ? "Resetting…" : "Erase everything & start fresh"}
          </button>
        </div>
      </div>
      <div className="module-toolbar">
        <div className="table-search">
          <Search size={16} />
          <input
            data-testid="client-search-input"
            className="inline-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by business, owner or email…"
          />
        </div>
        <span className="table-search-hint">{loading ? "Loading…" : `${filtered.length} of ${clients.length} accounts`}</span>
      </div>
      <div className="table-wrap">
        {loading ? (
          <div className="admin-loading">Loading client accounts…</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon"><Building2 size={22} /></div>
            <h3>No client accounts found</h3>
            <p>Client workspaces you create from setup requests will appear here.</p>
          </div>
        ) : (
          <table data-testid="client-accounts-table">
            <thead>
              <tr><th>Business</th><th>Owner</th><th>Plan</th><th>Invoices</th><th>Created</th><th></th></tr>
            </thead>
            <tbody>
              {filtered.slice(0, 200).map((client) => (
                <tr key={client.id} data-testid={`client-row-${client.id}`}>
                  <td><strong>{client.business_name || "—"}</strong></td>
                  <td>{client.email}<small>{client.name}</small></td>
                  <td><span className={`plan-pill ${client.plan === "pro" ? "pro" : "basic"}`}>{client.plan === "pro" ? "Pro" : "Starter"}</span></td>
                  <td>{client.invoice_count}</td>
                  <td>{shortDate(client.created_at)}</td>
                  <td>
                    <div className="client-row-actions">
                      <button
                        className="link-button"
                        data-testid={`open-client-${client.id}`}
                        disabled={openingId === client.id}
                        onClick={() => open(client)}
                      >
                        <LogIn size={14} /> {openingId === client.id ? "Opening…" : "Open account"}
                      </button>
                      <button
                        className="link-button accent"
                        data-testid={`bill-client-${client.id}`}
                        disabled={openingId === client.id}
                        onClick={() => open(client, { section: "invoices", createInvoice: true })}
                      >
                        <ReceiptText size={14} /> New invoice
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
